import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, Briefcase } from 'lucide-react';
import { Button } from '../Button';
import { besluitVerlenging, getB2bUpdates, getVerlengingen } from '../../services/gespreksverslagApi';

/**
 * Werkgeverskant van de gespreksverslag-engine (migratie 176).
 *  - Verlengingsverzoeken (Deel 5B): anoniem, alleen locatie/afdeling onder de
 *    privacygrendel. Pas na akkoord kan er in het traject weer geboekt worden.
 *  - B2B-updates (Deel 5): alleen nadat de medewerker toestemming gaf.
 * Een 403 (geen recht 'trajecten' of 'gespreksverslagen') verbergt de sectie.
 */

const STATUS: Record<string, { tekst: string; kleur: string }> = {
  PENDING_HR_AUTHORIZATION: { tekst: 'Wacht op akkoord', kleur: 'bg-amber-100 text-amber-900' },
  AUTHORIZED_EXTENSION: { tekst: 'Geaccordeerd', kleur: 'bg-green-100 text-green-800' },
  REJECTED: { tekst: 'Afgewezen', kleur: 'bg-gray-100 text-gray-700' },
};

export const Verlengingsverzoeken: React.FC = () => {
  const qc = useQueryClient();
  const { data: verzoeken = [], isError } = useQuery({ queryKey: ['verlengingen'], queryFn: getVerlengingen, retry: false });
  const [notitie, setNotitie] = useState<Record<string, string>>({});
  const besluit = useMutation({
    mutationFn: (v: { id: string; akkoord: boolean }) => besluitVerlenging(v.id, v.akkoord, notitie[v.id]),
    onSuccess: (_, v) => {
      toast.success(v.akkoord ? 'Verlenging geaccordeerd — er kan weer worden geboekt' : 'Verlenging afgewezen');
      qc.invalidateQueries({ queryKey: ['verlengingen'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Er ging iets mis'),
  });

  if (isError || verzoeken.length === 0) return null;
  return (
    <section id="verlengingen" className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3">
      <h2 className="flex items-center gap-2 font-semibold text-amber-900">
        <AlertTriangle className="w-5 h-5" aria-hidden />
        Verlengingsverzoeken
      </h2>
      <ul className="space-y-2">
        {verzoeken.map((v) => (
          <li key={v.id} className="rounded-md bg-white border border-amber-200 p-3 text-sm space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-gray-900">
                Een anonieme casus binnen <strong>{v.plek}</strong> heeft de limiet van {v.huidig_plafond} sessies bereikt.{' '}
                {v.professional} vraagt <strong>+{v.extra_sessies} sessies</strong>.
              </p>
              <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS[v.status].kleur}`}>{STATUS[v.status].tekst}</span>
            </div>
            {v.status === 'PENDING_HR_AUTHORIZATION' ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  className="flex-1 min-w-[200px] rounded-md border-gray-300 text-sm"
                  placeholder="Notitie (optioneel)"
                  maxLength={1000}
                  value={notitie[v.id] ?? ''}
                  onChange={(e) => setNotitie((n) => ({ ...n, [v.id]: e.target.value }))}
                />
                <Button size="sm" loading={besluit.isPending} onClick={() => besluit.mutate({ id: v.id, akkoord: true })}>Budget accorderen</Button>
                <Button size="sm" variant="outline" disabled={besluit.isPending} onClick={() => besluit.mutate({ id: v.id, akkoord: false })}>Afwijzen</Button>
              </div>
            ) : (
              v.notitie && <p className="text-xs text-gray-600">Notitie: {v.notitie}</p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export const B2bUpdates: React.FC = () => {
  const { data: updates = [], isError } = useQuery({ queryKey: ['b2b-updates'], queryFn: getB2bUpdates, retry: false });
  if (isError || updates.length === 0) return null;
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
      <div>
        <h2 className="flex items-center gap-2 font-semibold text-gray-900">
          <Briefcase className="w-5 h-5" aria-hidden />
          Management-updates
        </h2>
        <p className="text-xs text-gray-500">Functioneel werkvloeradvies, gedeeld met toestemming van de medewerker. Geen medische of privégegevens.</p>
      </div>
      <ul className="divide-y divide-gray-100">
        {updates.map((u) => (
          <li key={u.id} className="py-3 text-sm space-y-1">
            <p className="text-xs text-gray-500">
              {u.maand} · {u.plek} · {u.gesprek} · {u.professional} ({u.sjabloon})
            </p>
            <p className="text-gray-700"><span className="font-medium">Arbo-kader:</span> {u.arbo_kader}</p>
            {u.fte_belastbaarheid != null && (
              <p className="text-gray-700">
                <span className="font-medium">FTE-belastbaarheid:</span> {u.fte_belastbaarheid}% · <span className="font-medium">Verzuim:</span> {u.verwachte_verzuimreductie}
              </p>
            )}
            <p className="whitespace-pre-wrap text-gray-900">{u.werkvloer_advies}</p>
          </li>
        ))}
      </ul>
    </section>
  );
};
